import type { Article, Contrat, Justificatif, Vente } from '@troc/shared'
import { decomposerVente, eur, RESUME_BAREME, tableauDemarque, tauxEnPourcent } from '@troc/shared'
import { svgCode39 } from './codebarres'
import { echapper, imprimer } from './imprimer'

/**
 * Les trois documents que la boutique sort chaque jour : le ticket de caisse,
 * la planche d'étiquettes d'un dépôt, et le contrat signé.
 *
 * Les formats papier sont regroupés ici : c'est la seule chose à retoucher si
 * la boutique change d'imprimante.
 */

/** Rouleau thermique 80 mm — le standard des imprimantes de tickets. */
const LARGEUR_TICKET = '80mm'
/** Étiquette Dymo 11354 / Brother DK-11201. Une étiquette par page. */
const FORMAT_ETIQUETTE = '80mm 30mm'

/** Coordonnées reprises des documents officiels de la boutique. */
export const BOUTIQUE = {
  nom: 'TROC EN STOCK',
  baseline: 'Le dépôt-vente du particulier',
  adresse: 'Rue de l’Institut 66',
  localite: 'B-6780 MESSANCY',
  rpm: 'R.P.M. Arlon',
  tva: 'TVA BE 0458.731.509',
  iban: 'IBAN : BE86 2670 0215 6550',
  bic: 'Bic : GEBABEBBA',
  tel: 'Tél. +32 63 38 81 81',
  email: 'trocenstock@hotmail.com',
  site: 'www.trocenstock.com',
  horaires:
    'Ouvert tous les jours de 10 h à 18 h 30 (samedi de 10 h à 18 h) — ' +
    'Fermé dimanche et lundi — Juillet & août fermé à 18 h.',
}

/** Bandeaux de la charte : jaune en tête et en pied, bleu pour le texte. */
const JAUNE = '#ffd633'
const BLEU = '#1b4dd8'

const FORMAT_DATE_HEURE = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'short',
  timeStyle: 'short',
})
const FORMAT_DATE = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' })

// ── Ticket de caisse ────────────────────────────────────────────────────────

export function imprimerTicket(vente: Vente): void {
  const lignes = vente.lignes
    .map(
      (l) => `<tr>
        <td>${echapper(l.libelle)}<div class="ref">${echapper(l.ref)}${
          l.quantite > 1 ? ` · ${l.quantite} × ${eur(l.prixCentimes)}` : ''
        }</div></td>
        <td class="droite titre">${eur(l.prixCentimes * l.quantite)}</td>
      </tr>`,
    )
    .join('')

  const paiements = vente.paiements
    .map(
      (p) =>
        `<div class="ligne"><span>${p.mode === 'cb' ? 'Carte bancaire' : 'Espèces'}</span><span>${eur(p.montantCentimes)}</span></div>`,
    )
    .join('')

  const rendu =
    vente.rendueCentimes > 0
      ? `<div class="ligne titre"><span>Rendu</span><span>${eur(vente.rendueCentimes)}</span></div>`
      : ''

  imprimer(
    `Ticket ${vente.ticketNo}`,
    `<div class="ticket">
      <div class="entete">
        <div class="marque">${BOUTIQUE.nom}</div>
        <div class="petit">${BOUTIQUE.adresse} · ${BOUTIQUE.localite}</div>
        <div class="petit">${BOUTIQUE.tel} · ${BOUTIQUE.tva}</div>
      </div>
      <div class="petit meta">
        Ticket n° ${vente.ticketNo} · ${FORMAT_DATE_HEURE.format(new Date(vente.creeLe))}<br>
        Vendeur : ${echapper(vente.vendeurNom)}
      </div>
      <table>${lignes}</table>
      <div class="total">
        <div class="ligne grand titre"><span>TOTAL</span><span>${eur(vente.totalCentimes)}</span></div>
        ${paiements}
        ${rendu}
      </div>
      <div class="pied petit">
        Merci de votre visite.<br>
        Les articles vendus ne sont ni repris ni échangés.
      </div>
    </div>`,
    `@page { size: ${LARGEUR_TICKET} auto; margin: 4mm; }
     .ticket { width: 100%; font-size: 11px; line-height: 1.35; }
     .entete { text-align: center; padding-bottom: 6px; border-bottom: 2px solid #000; }
     .marque { font-weight: 800; font-size: 16px; letter-spacing: 0.04em; }
     .petit { font-size: 9px; }
     .meta { padding: 6px 0; border-bottom: 1px solid #000; }
     table { width: 100%; border-collapse: collapse; margin: 6px 0; }
     td { padding: 3px 0; vertical-align: top; }
     .ref { font-size: 8px; letter-spacing: 0.06em; }
     .total { border-top: 2px solid #000; padding-top: 6px; }
     .ligne { display: flex; justify-content: space-between; padding: 2px 0; }
     .grand { font-size: 15px; }
     .pied { text-align: center; margin-top: 10px; padding-top: 6px; border-top: 1px solid #000; }`,
  )
}

// ── Étiquettes ──────────────────────────────────────────────────────────────

export function imprimerEtiquettes(articles: Article[]): void {
  // Un article déposé en plusieurs exemplaires reçoit autant d'étiquettes :
  // les deux chaises identiques partent chacune avec la sienne.
  const etiquettes = articles
    .flatMap((article) => Array.from({ length: Math.max(1, article.quantite) }, () => article))
    .map(
      (article) => `<div class="etiquette">
        <div class="haut">
          <span class="marque">${BOUTIQUE.nom}</span>
          <span class="cat">${echapper(article.categorie)}</span>
        </div>
        <div class="designation">${echapper(article.titre)}</div>
        <div class="bas">
          <div class="code">${svgCode39(article.ref, { hauteur: 34 })}<div class="ref">${echapper(article.ref)}</div></div>
          <div class="prix">${eur(article.prixInitialCentimes)}</div>
        </div>
      </div>`,
    )
    .join('')

  imprimer(
    `Étiquettes (${articles.reduce((somme, a) => somme + Math.max(1, a.quantite), 0)})`,
    etiquettes,
    `@page { size: ${FORMAT_ETIQUETTE}; margin: 2mm; }
     .etiquette {
       width: 100%; height: 26mm; page-break-after: always; break-after: page;
       display: flex; flex-direction: column; justify-content: space-between;
     }
     .etiquette:last-child { page-break-after: auto; break-after: auto; }
     .haut { display: flex; justify-content: space-between; font-size: 6pt; letter-spacing: 0.08em; text-transform: uppercase; }
     .marque { font-weight: 800; }
     .designation { font-size: 8pt; font-weight: 800; line-height: 1.15; max-height: 2.4em; overflow: hidden; }
     .bas { display: flex; align-items: flex-end; justify-content: space-between; gap: 3mm; }
     .code { flex: 1; min-width: 0; }
     .code svg { width: 100%; height: 9mm; display: block; }
     .ref { font-size: 6pt; letter-spacing: 0.14em; text-align: center; }
     .prix { font-size: 14pt; font-weight: 800; letter-spacing: -0.02em; white-space: nowrap; }`,
  )
}

// ── Facture de sortie / justificatif de vente ───────────────────────────────

/**
 * Le document remis au client quand la boutique lui règle ses ventes.
 *
 * Reproduit à l'identique la disposition du `Justificatif.rtm` de l'ancien
 * logiciel (relevée le 29/08/2026 dans le binaire ReportBuilder) : adresse du
 * déposant, n° de facture et date d'émission en en-tête, puis un tableau sobre
 * Code / Libellé / Qté / Mt.unit / Total, et « Montant à verser » en pied.
 */
export function imprimerJustificatif(justificatif: Justificatif): void {
  const { deposant } = justificatif

  const lignes = justificatif.lignes
    .map(
      (l) => `<tr>
        <td class="ref">${echapper(l.ref)}</td>
        <td>${echapper(l.libelle)}</td>
        <td class="droite">${l.quantite}</td>
        <td class="droite">${eur(l.unitaireCentimes, { decimales: true })}</td>
        <td class="droite">${eur(l.totalCentimes, { decimales: true })}</td>
      </tr>`,
    )
    .join('')

  const localite = [
    deposant.codePostal && `${deposant.pays}-${deposant.codePostal}`,
    deposant.ville,
  ]
    .filter(Boolean)
    .join(' ')

  imprimer(
    `Justificatif ${justificatif.num}`,
    `<div class="adresse">
      ${echapper(deposant.titre ? `${deposant.titre} ` : '')}${echapper(deposant.nom)}<br>
      ${echapper(deposant.adresse)}<br>
      ${echapper(localite)}
    </div>

    <table class="identite">
      <tr><td class="cle">N° facture :</td><td class="valeur">${echapper(justificatif.num)}</td></tr>
      <tr><td class="cle">Date d’émission :</td><td class="valeur">${FORMAT_DATE_COURTE.format(new Date(justificatif.emisLe))}</td></tr>
    </table>

    <table class="articles">
      <thead><tr>
        <th>Code</th><th>Libellé</th>
        <th class="droite">Qté</th><th class="droite">Mt.unit</th><th class="droite">Total</th>
      </tr></thead>
      <tbody>${lignes}</tbody>
    </table>

    <div class="total">
      <span>Montant à verser</span>
      <span class="montant">${eur(justificatif.montantCentimes, { decimales: true })}</span>
    </div>`,
    `@page { size: A4; margin: 15mm; }
     .page, body { font-size: 10pt; font-family: Arial, sans-serif; }
     .adresse { margin-bottom: 6mm; }
     .identite { border-collapse: collapse; margin-bottom: 6mm; }
     .identite .cle { font-weight: 700; padding-right: 4mm; }
     table.articles { width: 100%; border-collapse: collapse; margin-bottom: 4mm; }
     .articles th, .articles td { padding: 1mm 2mm; }
     .articles th { font-weight: 700; text-align: center; border-bottom: 1px solid #000; }
     .articles th:first-child, .articles td.ref { text-align: left; }
     .total { display: flex; justify-content: space-between; border-top: 1px solid #000;
              padding-top: 2mm; font-weight: 700; }`,
  )
}

const FORMAT_DATE_COURTE = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short' })

// ── Contrat de dépôt ────────────────────────────────────────────────────────

export function imprimerContrat(contrat: Contrat): void {
  const lignes = contrat.articles
    .map((article) => {
      // Le contrat annonce la commission telle qu'elle s'appliquerait au prix
      // d'origine ; la démarque la recalculera au moment de la vente.
      const part = decomposerVente(article.prixInitialCentimes)
      return `<tr>
        <td class="ref">${echapper(article.ref)}</td>
        <td>${echapper(article.titre)}</td>
        <td>${echapper(article.categorie)}</td>
        <td class="droite">${eur(part.prixCentimes)}</td>
        <td class="droite">${tauxEnPourcent(part.taux)}</td>
        <td class="droite titre">${eur(part.netDeposantCentimes)}</td>
      </tr>`
    })
    .join('')

  const demarque = tableauDemarque(contrat.totalCentimes)
    .map(
      (ligne) =>
        `<tr><td>${echapper(ligne.libelle)}</td><td class="droite titre">${eur(ligne.prixCentimes)}</td></tr>`,
    )
    .join('')

  const signature = contrat.signatureSvg
    ? `<svg viewBox="0 0 360 200" class="paraphe">${contrat.signatureSvg}</svg>`
    : '<div class="paraphe"></div>'

  imprimer(
    `Contrat ${contrat.num}`,
    `<div class="page">
      <div class="entete">
        <div>
          <div class="marque">${BOUTIQUE.nom}</div>
          <div class="petit">
            ${BOUTIQUE.adresse}<br>${BOUTIQUE.localite}<br>
            ${BOUTIQUE.tva}<br>${BOUTIQUE.tel}
          </div>
        </div>
        <div class="droite">
          <div class="kicker">CONTRAT DE DÉPÔT</div>
          <div class="numero">${echapper(contrat.num)}</div>
        </div>
      </div>

      <div class="parties">
        <div>
          <div class="kicker">Déposant</div>
          ${echapper(contrat.deposant.nom)} · ${echapper(contrat.deposant.ref)}<br>
          ${echapper(contrat.deposant.tel)}<br>
          ${echapper(contrat.deposant.adresse)}
        </div>
        <div>
          <div class="kicker">Période</div>
          Dépôt du ${FORMAT_DATE.format(new Date(contrat.creeLe))}<br>
          Démarque jusqu'au ${FORMAT_DATE.format(new Date(contrat.finDemarque))}<br>
          Reçu par ${echapper(contrat.vendeurNom)}
        </div>
      </div>

      <table class="articles">
        <thead><tr>
          <th>N°</th><th>Désignation</th><th>Catégorie</th>
          <th class="droite">Prix</th><th class="droite">Comm.</th><th class="droite">Net déposant</th>
        </tr></thead>
        <tbody>${lignes}</tbody>
      </table>

      <div class="total">
        <span class="titre">TOTAL DÉPOSÉ</span>
        <span class="montant">${eur(contrat.totalCentimes)}</span>
      </div>

      <div class="colonnes">
        <div>
          <div class="kicker">Démarque automatique</div>
          <table class="petite"><tbody>${demarque}</tbody></table>
        </div>
        <div>
          <div class="kicker">Conditions</div>
          <p class="petit">
            Dépôt gratuit. ${echapper(RESUME_BAREME)}
            Contrat d'un mois : au-delà, l'article non vendu peut être récupéré sans frais ou rester
            en rayon, son prix baissant alors chaque mois — −10 %, puis −20 %, −40 %, −60 % et
            −80 % au plus bas.
            Les articles doivent être propres et en état de fonctionnement.
            Le paiement des ventes est effectué en espèces après le 15 de chaque mois,
            et par virement bancaire au-delà de 3 000 €.
          </p>
        </div>
      </div>

      <div class="signatures">
        <div>
          <div class="kicker">Signature du déposant</div>
          ${signature}
        </div>
        <div>
          <div class="kicker">Pour la boutique</div>
          <div class="paraphe nom">${echapper(contrat.vendeurNom)}</div>
        </div>
      </div>
    </div>`,
    `@page { size: A4; margin: 12mm; }
     .page { font-size: 10pt; line-height: 1.4; }
     .entete { display: flex; justify-content: space-between; align-items: flex-start;
               border-bottom: 2px solid #000; padding-bottom: 8px; }
     .marque { font-weight: 800; font-size: 15pt; letter-spacing: -0.01em; }
     .numero { font-weight: 800; font-size: 13pt; }
     .kicker { font-size: 7pt; letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 3px; }
     .petit { font-size: 8pt; }
     .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
                padding: 10px 0; border-bottom: 1px solid #000; font-size: 9pt; }
     table { width: 100%; border-collapse: collapse; }
     .articles { margin: 10px 0; font-size: 9pt; }
     .articles th { text-align: left; font-size: 7pt; letter-spacing: 0.08em; text-transform: uppercase;
                    border-bottom: 2px solid #000; padding: 4px 3px; }
     .articles td { padding: 4px 3px; border-bottom: 1px solid #999; }
     .ref { font-size: 8pt; letter-spacing: 0.06em; }
     .total { display: flex; justify-content: space-between; align-items: baseline;
              border-top: 2px solid #000; padding-top: 8px; }
     .montant { font-weight: 800; font-size: 20pt; letter-spacing: -0.02em; }
     .colonnes { display: grid; grid-template-columns: 1fr 1.2fr; gap: 24px; margin-top: 16px; }
     .petite { font-size: 8pt; }
     .petite td { padding: 2px 0; border-bottom: 1px solid #ddd; }
     .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 24px; }
     .paraphe { display: block; width: 100%; height: 22mm; border-bottom: 1px solid #000; }
     .paraphe.nom { display: flex; align-items: flex-end; font-size: 10pt; padding-bottom: 3px; }
     .paraphe path { fill: none; stroke: #000; stroke-width: 2.4; stroke-linecap: round; }`,
  )
}

// ── Bon de reprise et bordereau de don ──────────────────────────────────────

export interface SortieImprimable {
  id: number
  num: string
  type: 'reprise' | 'don'
  creeLe: string
  notes: string
  vendeurNom: string
  signatureSvg: string | null
  deposant: {
    nom: string
    ref: string
    gsm: string
    tel: string
    adresse: string
    codePostal: string
    ville: string
  } | null
  lignes: { ref: string; libelle: string; quantite: number; etat: string }[]
}

/**
 * Le document qui accompagne une sortie d'articles.
 *
 * Deux usages sous une même forme. La **reprise** est signée par le déposant :
 * c'est la preuve que ses pièces sont reparties avec lui, et elle protège la
 * boutique le jour où il affirme le contraire. Le **bordereau de don** n'a pas
 * de signataire — il énumère ce qui est parti à la Croix-Rouge, ce qui sert
 * autant à l'association qu'à la boutique pour montrer qu'elle n'a rien vendu.
 *
 * Les pièces introuvables figurent sur le bon, marquées comme telles. Les
 * cacher arrangerait tout le monde sur le moment et ne tromperait personne
 * plus tard.
 */
export function imprimerSortie(sortie: SortieImprimable): void {
  const don = sortie.type === 'don'

  const lignes = sortie.lignes
    .map(
      (l) => `<tr>
        <td class="ref">${echapper(l.ref)}</td>
        <td>${echapper(l.libelle)}</td>
        <td class="droite">${l.quantite}</td>
        <td class="droite">${
          l.etat === 'introuvable' ? '<strong>introuvable</strong>' : don ? 'donné' : 'rendu'
        }</td>
      </tr>`,
    )
    .join('')

  const rendus = sortie.lignes.filter((l) => l.etat !== 'introuvable').length
  const introuvables = sortie.lignes.filter((l) => l.etat === 'introuvable').length

  const signature = sortie.signatureSvg
    ? `<svg viewBox="0 0 360 200" class="paraphe">${sortie.signatureSvg}</svg>`
    : '<div class="paraphe"></div>'

  const client = sortie.deposant
    ? `${echapper(sortie.deposant.nom)} · ${echapper(sortie.deposant.ref)}<br>
       ${echapper([sortie.deposant.adresse, [sortie.deposant.codePostal, sortie.deposant.ville].filter(Boolean).join(' ')].filter(Boolean).join(', '))}<br>
       ${echapper(sortie.deposant.gsm || sortie.deposant.tel)}`
    : 'Croix-Rouge'

  imprimer(
    don ? `Bordereau de don ${sortie.num}` : `Bon de reprise ${sortie.num}`,
    `<div class="page">
      <div class="entete">
        <div>
          <div class="marque">${BOUTIQUE.nom}</div>
          <div class="petit">${BOUTIQUE.adresse} · ${BOUTIQUE.localite}</div>
          <div class="petit">${BOUTIQUE.tel} · ${BOUTIQUE.tva}</div>
        </div>
        <div class="droite">
          <div class="ref">${don ? 'BORDEREAU DE DON' : 'BON DE REPRISE'}</div>
          <div class="numero">${echapper(sortie.num)}</div>
          <div class="petit">${FORMAT_DATE.format(new Date(sortie.creeLe))}</div>
        </div>
      </div>

      <div class="bloc">
        <div class="cle">${don ? 'Bénéficiaire' : 'Déposant'}</div>
        <div>${client}</div>
      </div>

      <table class="grille">
        <thead>
          <tr><th>N°</th><th>Désignation</th><th class="droite">Qté</th><th class="droite">État</th></tr>
        </thead>
        <tbody>${lignes}</tbody>
      </table>

      <div class="total">
        <span>${don ? 'Pièces données' : 'Pièces restituées'}</span>
        <span class="titre">${rendus}${introuvables > 0 ? ` · ${introuvables} introuvable${introuvables > 1 ? 's' : ''}` : ''}</span>
      </div>

      ${
        don
          ? `<p class="petit">Ces pièces, non réclamées au terme du contrat de dépôt, ont été
             remises à titre gratuit. Elles n'ont fait l'objet d'aucune vente et aucune somme
             n'est due au déposant.</p>`
          : `<p class="petit">Le déposant reconnaît avoir repris les articles énumérés ci-dessus.
             Les pièces marquées « introuvable » n'ont pu être restituées et restent à rechercher.</p>`
      }

      <div class="signatures">
        ${
          don
            ? ''
            : `<div><div class="cle">Signature du déposant</div>${signature}</div>`
        }
        <div><div class="cle">Pour la boutique</div><div class="paraphe">${echapper(sortie.vendeurNom)}</div></div>
      </div>
    </div>`,
    `@page { size: A4; margin: 14mm; }
     .page { font-size: 12px; }
     .entete { display: flex; justify-content: space-between; border-bottom: 2px solid #000; padding-bottom: 10px; }
     .marque { font-weight: 800; font-size: 18px; letter-spacing: 0.02em; }
     .numero { font-weight: 800; font-size: 17px; }
     .petit { font-size: 10px; }
     .droite { text-align: right; }
     .ref { font-size: 10px; letter-spacing: 0.06em; }
     .cle { font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: #555; }
     .bloc { padding: 12px 0; border-bottom: 1px solid #000; }
     .grille { width: 100%; border-collapse: collapse; margin: 12px 0; }
     .grille th { text-align: left; font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase; border-bottom: 2px solid #000; padding: 4px 6px; }
     .grille td { padding: 5px 6px; border-bottom: 1px solid #ccc; }
     .total { display: flex; justify-content: space-between; border-top: 2px solid #000; padding-top: 8px; font-size: 14px; }
     .titre { font-weight: 800; }
     .signatures { display: flex; gap: 40px; margin-top: 26px; }
     .signatures > div { flex: 1; }
     .paraphe { display: block; width: 100%; height: 54px; border-bottom: 1px solid #000; }`,
  )
}
